/**
 * V37.57 LEGALCOOP — Back-office admin et Espace juriste.
 *
 * Page mixte (admin + juriste) :
 *  - admin/super_admin : voit toutes les demandes, peut creer une demande
 *    rattachee a un projet CCMI, marquer payee, exonerer, assigner un juriste.
 *  - juriste : voit UNIQUEMENT ses demandes assignees ; peut cloturer.
 *
 * Mentions obligatoires affichees en tete (PDF / vitrine / back-office).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, ArrowLeft, RefreshCw, Scale, CreditCard, Gift, UserPlus, CheckCircle2, FileDown, Link2, FileText, Coins, Settings2 } from 'lucide-react';
import { LEGALCOOP_OFFICIAL_DEFINITION, LEGALCOOP_DISCLAIMERS as SHARED_DISCLAIMERS } from '../constants/legalcoop';
import LegalcoopModals from './LegalcoopModals';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  pending_payment: { label: 'En attente paiement', color: 'bg-amber-600 text-white border-amber-800 font-semibold' },
  paid: { label: 'Payé', color: 'bg-blue-700 text-white border-blue-900 font-semibold' },
  exempted: { label: 'Exonéré', color: 'bg-purple-700 text-white border-purple-900 font-semibold' },
  assigned: { label: 'Assigné juriste', color: 'bg-cyan-700 text-white border-cyan-900 font-semibold' },
  in_review: { label: 'En revue', color: 'bg-indigo-700 text-white border-indigo-900 font-semibold' },
  completed: { label: 'Terminé', color: 'bg-emerald-700 text-white border-emerald-900 font-semibold' },
  cancelled: { label: 'Annulé', color: 'bg-red-700 text-white border-red-900 font-semibold' },
};

const DISCLAIMERS = SHARED_DISCLAIMERS;

const getAuthHeader = () => {
  const t = localStorage.getItem('admin_token')
        || localStorage.getItem('token')
        || localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function LegalcoopDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleView, setRoleView] = useState('');
  const [actionModal, setActionModal] = useState(null); // {kind, item}
  const [actionInput, setActionInput] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);
  // V37.61 — Generation lien Stripe pre-rempli par email (admin)
  const [linkModal, setLinkModal] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkNote, setLinkNote] = useState('');
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [linkResult, setLinkResult] = useState(null); // {checkout_url, request_id, customer_email}
  // V37.76 — Regles credits (Super Admin)
  const [creditConfigModal, setCreditConfigModal] = useState(false);
  const [creditConfig, setCreditConfig] = useState(null);
  const [creditRoles, setCreditRoles] = useState([]);
  const [creditConfigLoading, setCreditConfigLoading] = useState(false);
  const [creditConfigSaving, setCreditConfigSaving] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('limit', '100');
      const res = await axios.get(`${API}/legalcoop/requests?${params.toString()}`, { headers: getAuthHeader() });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setRoleView(res.data?.role_view || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement LEGALCOOP');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const isAdmin = ['admin', 'super_admin'].includes(roleView);
  const isJuriste = roleView === 'juriste';

  const submitAction = async () => {
    if (!actionModal) return;
    const { kind, item } = actionModal;
    setActionSubmitting(true);
    try {
      if (kind === 'mark_paid') {
        await axios.post(`${API}/legalcoop/requests/${item.id}/mark-paid`,
          { note: actionInput || null, payment_reference: null }, { headers: getAuthHeader() });
        toast.success('Demande marquée comme payée');
      } else if (kind === 'exempt') {
        if ((actionInput || '').trim().length < 5) {
          toast.error('Justification d\'exonération requise (min 5 caractères)');
          setActionSubmitting(false);
          return;
        }
        await axios.post(`${API}/legalcoop/requests/${item.id}/exempt`,
          { reason: actionInput.trim() }, { headers: getAuthHeader() });
        toast.success('Demande exonérée');
      } else if (kind === 'assign') {
        const email = (actionInput || '').trim().toLowerCase();
        if (!email.includes('@') || !email.includes('.')) {
          toast.error('Email juriste invalide');
          setActionSubmitting(false);
          return;
        }
        await axios.post(`${API}/legalcoop/requests/${item.id}/assign-juriste`,
          { juriste_email: email }, { headers: getAuthHeader() });
        toast.success(`Juriste assigné : ${email}`);
      } else if (kind === 'complete') {
        await axios.post(`${API}/legalcoop/requests/${item.id}/complete`,
          { note: actionInput || null }, { headers: getAuthHeader() });
        toast.success('Revue marquée terminée');
      } else if (kind === 'stripe') {
        const res = await axios.post(`${API}/legalcoop/requests/${item.id}/stripe-checkout`,
          { origin_url: window.location.origin }, { headers: getAuthHeader() });
        const url = res.data?.checkout_url;
        if (url) {
          window.open(url, '_blank');
          toast.success('Lien Stripe ouvert dans un nouvel onglet');
        }
      } else if (kind === 'invoice') {
        // V37.58 — Telecharger la facture PDF
        const res = await axios.get(`${API}/legalcoop/requests/${item.id}/facture-pdf`,
          { headers: getAuthHeader(), responseType: 'blob' });
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${res.headers?.['x-invoice-number'] || 'facture-legalcoop'}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Facture téléchargée (${res.headers?.['x-invoice-number'] || 'PDF'})`);
      }
      setActionModal(null);
      setActionInput('');
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur action');
    } finally {
      setActionSubmitting(false);
    }
  };

  const counters = items.reduce((acc, it) => {
    acc[it.status] = (acc[it.status] || 0) + 1;
    return acc;
  }, {});

  // V37.61 — Generation lien Stripe pre-rempli pour un membre par email
  const submitPaymentLink = async () => {
    const email = (linkEmail || '').trim().toLowerCase();
    if (!email.includes('@') || !email.includes('.')) {
      toast.error('Email invalide');
      return;
    }
    setLinkSubmitting(true);
    setLinkResult(null);
    try {
      const res = await axios.post(
        `${API}/legalcoop/admin/payment-link`,
        { email, origin_url: window.location.origin, note: linkNote || null },
        { headers: getAuthHeader() },
      );
      setLinkResult({
        checkout_url: res.data?.checkout_url,
        request_id: res.data?.request_id,
        customer_email: res.data?.customer_email,
        amount_ttc: res.data?.amount_ttc,
      });
      toast.success(`Lien Stripe généré pour ${email}`);
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur génération lien Stripe');
    } finally {
      setLinkSubmitting(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Lien copié dans le presse-papier');
    } catch {
      toast.error('Impossible de copier — sélectionnez et copiez manuellement');
    }
  };

  // V37.76 — Regles credits configurables (Super Admin)
  const openCreditConfig = async () => {
    setCreditConfigModal(true);
    setCreditConfigLoading(true);
    try {
      const res = await axios.get(`${API}/legalcoop/admin/credit-config`, { headers: getAuthHeader() });
      setCreditConfig(res.data?.config || null);
      setCreditRoles(res.data?.valid_exempt_roles || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement règles crédits');
      setCreditConfigModal(false);
    } finally {
      setCreditConfigLoading(false);
    }
  };

  const saveCreditConfig = async () => {
    if (!creditConfig) return;
    setCreditConfigSaving(true);
    try {
      const services = {};
      Object.entries(creditConfig.services || {}).forEach(([st, rule]) => {
        services[st] = {
          credits_enabled: !!rule.credits_enabled,
          price_credits_override: rule.price_credits_override === '' || rule.price_credits_override == null
            ? null : Number(rule.price_credits_override),
        };
      });
      const res = await axios.put(
        `${API}/legalcoop/admin/credit-config`,
        {
          credits_enabled: !!creditConfig.credits_enabled,
          rate_eur_per_credit: Number(creditConfig.rate_eur_per_credit) || 1,
          services,
          exempt_roles: creditConfig.exempt_roles || [],
        },
        { headers: getAuthHeader() },
      );
      setCreditConfig(res.data?.config || null);
      toast.success('Règles crédits enregistrées');
      setCreditConfigModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur enregistrement règles crédits');
    } finally {
      setCreditConfigSaving(false);
    }
  };

  const setServiceRule = (st, patch) => {
    setCreditConfig((c) => ({
      ...c,
      services: { ...c.services, [st]: { ...c.services[st], ...patch } },
    }));
  };

  const toggleExemptRole = (role) => {
    setCreditConfig((c) => {
      const cur = c.exempt_roles || [];
      return {
        ...c,
        exempt_roles: cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role],
      };
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-slate-50 py-8 px-4" data-testid="legalcoop-dashboard">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 rounded-lg hover:bg-white/80 transition-colors border border-slate-200"
              title="Retour"
            >
              <ArrowLeft className="h-5 w-5 text-slate-700" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Scale className="h-6 w-6 text-indigo-700" />
                LEGALCOOP — Revue de conformité CCMI
              </h1>
              <div className="text-sm text-slate-500">
                Forfait Revue de conformité CCMI — 500 € HT (600 € TTC)
                {roleView && <Badge className="ml-2 text-[10px] bg-slate-800 text-white border-slate-900 font-semibold">vue : {roleView}</Badge>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {roleView === 'super_admin' && (
              <Button variant="outline"
                className="border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100"
                onClick={openCreditConfig}
                data-testid="legalcoop-credit-config-btn">
                <Settings2 className="h-4 w-4 mr-2" /> Règles crédits
              </Button>
            )}
            {isAdmin && (
              <Button variant="default"
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                onClick={() => navigate('/admin/legalcoop/finance')}
                data-testid="legalcoop-finance-link-btn">
                <FileDown className="h-4 w-4 mr-2" /> Gestion financière
              </Button>
            )}
            {isAdmin && (
              <Button variant="default"
                className="bg-indigo-700 hover:bg-indigo-800 text-white"
                onClick={() => { setLinkModal(true); setLinkEmail(''); setLinkNote(''); setLinkResult(null); }}
                data-testid="legalcoop-generate-link-btn">
                <Link2 className="h-4 w-4 mr-2" /> Générer lien Stripe (email)
              </Button>
            )}
            <Button variant="outline" onClick={fetchList} disabled={loading} data-testid="legalcoop-refresh-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Actualiser
            </Button>
          </div>
        </div>

        {/* Disclaimers obligatoires */}
        <Card className="border-amber-300 bg-amber-50" data-testid="legalcoop-disclaimers">
          <CardContent className="pt-4 space-y-2">
            <p className="text-xs text-slate-800 leading-relaxed" data-testid="legalcoop-official-definition">
              {LEGALCOOP_OFFICIAL_DEFINITION}
            </p>
            <div className="border-t border-amber-200 pt-2 space-y-1">
              {DISCLAIMERS.map((d, i) => (
                <p key={i} className="text-xs text-amber-800 italic">⚠ {d}</p>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Compteurs */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="legalcoop-counters">
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <Card key={key}>
              <CardContent className="pt-4">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">{meta.label}</p>
                <p className="text-2xl font-bold text-slate-800 mt-1" data-testid={`legalcoop-counter-${key}`}>
                  {counters[key] || 0}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filtres */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Filtres</CardTitle></CardHeader>
          <CardContent>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-64" data-testid="legalcoop-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Liste */}
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Demandes ({total})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 flex items-center justify-center text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement...
              </div>
            ) : items.length === 0 ? (
              <p className="py-8 text-center text-slate-400 italic" data-testid="legalcoop-empty">
                Aucune demande LEGALCOOP.
              </p>
            ) : (
              <div className="space-y-2" data-testid="legalcoop-list">
                {items.map((it) => {
                  const meta = STATUS_META[it.status] || STATUS_META.pending_payment;
                  return (
                    <div
                      key={it.id}
                      className="border border-slate-200 rounded p-3 hover:bg-slate-50 transition-colors"
                      data-testid={`legalcoop-item-${it.id}`}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
                            <span className="font-mono text-xs text-slate-700">CCMI {it.ccmi_project_reference || '-'}</span>
                            <span className="text-[10px] text-slate-400">{it.id.slice(0, 8)}</span>
                            <span className="text-[10px] text-indigo-700 font-semibold">{it.amount_ht} € HT</span>
                            {it.payment_provider === 'credits' && (
                              <Badge className="text-[10px] bg-amber-700 text-white border-amber-900 font-semibold"
                                data-testid={`legalcoop-paid-credits-${it.id}`}>
                                <Coins className="h-3 w-3 mr-1" /> Payé par crédits ({it.credits_spent})
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-600 mt-1">
                            Demandeur <b>{it.requester_email}</b>
                            {it.assigned_juriste && <> • Juriste <b>{it.assigned_juriste}</b></>}
                          </p>
                          {it.note && <p className="text-xs text-slate-500 mt-1 italic">« {it.note} »</p>}
                          {it.exemption_reason && <p className="text-xs text-purple-700 mt-1 italic">Exonération : {it.exemption_reason}</p>}
                        </div>
                        <div className="flex items-center gap-1 flex-wrap" data-testid={`legalcoop-actions-${it.id}`}>
                          {/* Actions selon statut + role */}
                          {it.status === 'pending_payment' && (
                            <>
                              <Button size="sm" variant="outline" className="h-7 text-xs bg-blue-50 border-blue-200 text-blue-700"
                                onClick={() => { setActionModal({ kind: 'stripe', item: it }); setActionInput(''); }}
                                data-testid={`legalcoop-action-stripe-${it.id}`}>
                                <CreditCard className="h-3 w-3 mr-1" /> Lien Stripe
                              </Button>
                              {isAdmin && (
                                <>
                                  <Button size="sm" variant="outline" className="h-7 text-xs bg-emerald-50 border-emerald-200 text-emerald-700"
                                    onClick={() => { setActionModal({ kind: 'mark_paid', item: it }); setActionInput(''); }}
                                    data-testid={`legalcoop-action-markpaid-${it.id}`}>
                                    <CheckCircle2 className="h-3 w-3 mr-1" /> Marquer payé
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-7 text-xs bg-purple-50 border-purple-200 text-purple-700"
                                    onClick={() => { setActionModal({ kind: 'exempt', item: it }); setActionInput(''); }}
                                    data-testid={`legalcoop-action-exempt-${it.id}`}>
                                    <Gift className="h-3 w-3 mr-1" /> Exonérer
                                  </Button>
                                </>
                              )}
                            </>
                          )}
                          {isAdmin && ['paid', 'exempted', 'in_review', 'assigned', 'completed'].includes(it.status) && (
                            <>
                              <Button size="sm" variant="outline" className="h-7 text-xs bg-emerald-50 border-emerald-300 text-emerald-800"
                                onClick={async () => {
                                  try {
                                    const res = await axios.post(`${API}/legalcoop/finance/recus`,
                                      { request_id: it.id }, { headers: getAuthHeader() });
                                    toast.success(`Reçu ${res.data?.recu?.recu_number} émis`);
                                  } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur reçu'); }
                                }}
                                data-testid={`legalcoop-action-recu-${it.id}`}>
                                <FileText className="h-3 w-3 mr-1" /> Émettre reçu
                              </Button>
                              {it.invoice_number && (
                                <Button size="sm" variant="outline" className="h-7 text-xs bg-red-50 border-red-300 text-red-800"
                                  onClick={async () => {
                                    const motif = window.prompt(`Motif de l'avoir sur ${it.invoice_number} ?`);
                                    if (!motif || motif.trim().length < 3) return;
                                    try {
                                      const res = await axios.post(`${API}/legalcoop/finance/avoirs`,
                                        { request_id: it.id, motif: motif.trim() }, { headers: getAuthHeader() });
                                      toast.success(`Avoir ${res.data?.avoir?.credit_number} émis (sans remboursement)`);
                                    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur avoir'); }
                                  }}
                                  data-testid={`legalcoop-action-avoir-${it.id}`}>
                                  <Gift className="h-3 w-3 mr-1" /> Émettre avoir
                                </Button>
                              )}
                            </>
                          )}
                          {isAdmin && ['paid', 'exempted', 'in_review', 'assigned'].includes(it.status) && (
                            <Button size="sm" variant="outline" className="h-7 text-xs bg-cyan-50 border-cyan-200 text-cyan-700"
                              onClick={() => { setActionModal({ kind: 'assign', item: it }); setActionInput(it.assigned_juriste || ''); }}
                              data-testid={`legalcoop-action-assign-${it.id}`}>
                              <UserPlus className="h-3 w-3 mr-1" /> {it.assigned_juriste ? 'Réassigner' : 'Assigner juriste'}
                            </Button>
                          )}
                          {(isAdmin || (isJuriste && it.assigned_juriste === roleView)) && ['in_review', 'assigned'].includes(it.status) && (
                            <Button size="sm" variant="outline" className="h-7 text-xs bg-emerald-50 border-emerald-200 text-emerald-700"
                              onClick={() => { setActionModal({ kind: 'complete', item: it }); setActionInput(''); }}
                              data-testid={`legalcoop-action-complete-${it.id}`}>
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Terminer
                            </Button>
                          )}
                          {/* V37.58 — Telecharger facture (disponible si paye / en revue / termine) */}
                          {['paid', 'exempted', 'in_review', 'assigned', 'completed'].includes(it.status) && (
                            <Button size="sm" variant="outline" className="h-7 text-xs bg-slate-50 border-slate-200 text-slate-700"
                              onClick={() => { setActionModal({ kind: 'invoice', item: it }); setActionInput(''); }}
                              data-testid={`legalcoop-action-invoice-${it.id}`}>
                              <FileDown className="h-3 w-3 mr-1" />
                              {it.invoice_number ? 'Re-télécharger facture' : 'Télécharger facture'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <LegalcoopModals
        actionModal={actionModal} setActionModal={setActionModal} actionInput={actionInput} setActionInput={setActionInput} actionSubmitting={actionSubmitting} submitAction={submitAction}
        linkModal={linkModal} setLinkModal={setLinkModal} linkResult={linkResult} setLinkResult={setLinkResult} linkEmail={linkEmail} setLinkEmail={setLinkEmail} linkNote={linkNote} setLinkNote={setLinkNote}
        linkSubmitting={linkSubmitting} submitPaymentLink={submitPaymentLink} copyToClipboard={copyToClipboard}
        creditConfigModal={creditConfigModal} setCreditConfigModal={setCreditConfigModal} creditConfig={creditConfig} setCreditConfig={setCreditConfig} creditRoles={creditRoles}
        creditConfigLoading={creditConfigLoading} creditConfigSaving={creditConfigSaving} saveCreditConfig={saveCreditConfig} setServiceRule={setServiceRule} toggleExemptRole={toggleExemptRole}
      />
    </div>
  );
}
